import type {FirebaseOptions} from 'firebase/app'
import type {Messaging} from 'firebase/messaging'
import apiClient, {PutUserDeviceBody} from 'src/services/apiClient'
import {dispatch, store} from 'src/redux-store'
import {setWebDeviceTokens, WebDeviceToken} from 'src/slices/userSlice'

// Web push notifications for the therapy reminders (specs/2026-10-notification-push.md in
// therapy-service). The browser registers with Firebase Cloud Messaging and the resulting token
// is added to the user's devices (users.webDeviceTokens, at most 5) through psp-backend; the
// notification-manager then sends the reminders to each of them. Firebase is imported lazily: only users who enable notifications
// download it.

export type PushConfig = {
  enabled: boolean
  vapidKey?: string
  firebase?: FirebaseOptions
}

// - unsupported: the browser cannot receive web push at all
// - ios-install-required: iPhone/iPad in Safari - web push works only from the app added to the
//   Home Screen (iOS 16.4+)
// - not-configured: no Firebase configuration for this environment (GET /push-config)
// - denied: the user blocked notifications for the portal in the browser settings
// - inactive: available, not enabled on this device (or dropped from the user's devices)
// - active: this device is among the user's devices receiving the reminders
export type PushState = 'unsupported' | 'ios-install-required' | 'not-configured' | 'denied' | 'inactive' | 'active'

// The token this browser last registered: lets the portal tell whether this device is among
// users.webDeviceTokens, and spot a token rotated by Firebase.
const LAST_TOKEN_KEY = 'webPush.lastToken'
// The user who registered it: a token missing from the list is registered again only for that
// user, never for another one logging in on the same browser (RF7).
const LAST_TOKEN_USER_KEY = 'webPush.lastTokenUserId'
// A device already in the list is saved again at most once a day, to keep its lastSeenAt current
const LAST_SEEN_REFRESH_MS = 24 * 60 * 60 * 1000
// Same limits as psp-backend PUT /user-data/device
const MAX_DEVICES = 5
const MAX_LABEL_LENGTH = 100
const SERVICE_WORKER_URL = '/service-worker.js'

let configPromise: Promise<PushConfig> | undefined
let messagingPromise: Promise<Messaging> | undefined

function readItem(key: string): string | null {
  try {
    return window.localStorage.getItem(key)
  } catch (error) {
    return null
  }
}

function writeItem(key: string, value: string | null | undefined): void {
  try {
    if (value) {
      window.localStorage.setItem(key, value)
    } else {
      window.localStorage.removeItem(key)
    }
  } catch (error) {
    // storage unavailable (private mode): the portal simply cannot remember the device
  }
}

function readLastToken(): string | null {
  return readItem(LAST_TOKEN_KEY)
}

function currentUserId(): string | undefined {
  return store.getState().userReducer.data?._id || undefined
}

export function tokensOf(webDeviceTokens?: WebDeviceToken[]): string[] {
  return (webDeviceTokens ?? []).map(device => device.token)
}

export function isIos(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
}

export function isStandalone(): boolean {
  return window.matchMedia?.('(display-mode: standalone)').matches || (navigator as any).standalone === true
}

// "Chrome · macOS", "Safari · iPhone · app installata": browser and operating system only, never
// the whole user agent (data minimisation), to tell the user's devices apart.
export function getDeviceLabel(nav: Navigator = navigator, standalone: boolean = isStandalone()): string {
  const userAgentData = (nav as any).userAgentData as {brands?: {brand: string}[], platform?: string} | undefined
  const browser = browserFromBrands(userAgentData?.brands) ?? browserFromUserAgent(nav.userAgent ?? '')
  const os = osFromPlatform(userAgentData?.platform) ?? osFromUserAgent(nav.userAgent ?? '', nav)
  const parts = [browser, os, standalone ? 'app installata' : undefined].filter(Boolean)
  return parts.join(' · ').slice(0, MAX_LABEL_LENGTH)
}

const BRAND_NAMES: [RegExp, string][] = [
  [/edge/i, 'Edge'],
  [/opera/i, 'Opera'],
  [/samsung/i, 'Samsung Internet'],
  [/google chrome/i, 'Chrome'],
  [/chromium/i, 'Chromium'],
]

function browserFromBrands(brands?: {brand: string}[]): string | undefined {
  const names = (brands ?? []).map(({brand}) => brand)
  for (const [pattern, name] of BRAND_NAMES) {
    if (names.some(brand => pattern.test(brand))) return name
  }
  return undefined
}

function browserFromUserAgent(userAgent: string): string | undefined {
  if (/Edg(e|A|iOS)?\//.test(userAgent)) return 'Edge'
  if (/OPR\/|Opera/.test(userAgent)) return 'Opera'
  if (/SamsungBrowser\//.test(userAgent)) return 'Samsung Internet'
  if (/Firefox\/|FxiOS\//.test(userAgent)) return 'Firefox'
  if (/Chrome\/|CriOS\//.test(userAgent)) return 'Chrome'
  if (/Safari\//.test(userAgent)) return 'Safari'
  return undefined
}

function osFromPlatform(platform?: string): string | undefined {
  if (!platform || platform === 'Unknown') return undefined
  return platform === 'Chrome OS' ? 'ChromeOS' : platform
}

function osFromUserAgent(userAgent: string, nav: Navigator): string | undefined {
  if (/iPhone|iPod/.test(userAgent)) return 'iPhone'
  // iPadOS Safari reports itself as a Mac: told apart by the touch screen
  if (/iPad/.test(userAgent) || (/Macintosh/.test(userAgent) && nav.maxTouchPoints > 1)) return 'iPad'
  if (/Android/.test(userAgent)) return 'Android'
  if (/Windows/.test(userAgent)) return 'Windows'
  if (/Macintosh|Mac OS X/.test(userAgent)) return 'macOS'
  if (/CrOS/.test(userAgent)) return 'ChromeOS'
  if (/Linux/.test(userAgent)) return 'Linux'
  return undefined
}

function hasPushApis(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
}

export function loadPushConfig(): Promise<PushConfig> {
  if (!configPromise) {
    configPromise = apiClient.getPushConfig()
      .then((config: PushConfig) => config ?? {enabled: false})
      .catch(() => ({enabled: false}))
  }
  return configPromise
}

async function getMessagingInstance(config: PushConfig): Promise<Messaging> {
  if (!messagingPromise) {
    messagingPromise = (async () => {
      const [{initializeApp, getApps}, {getMessaging}] = await Promise.all([
        import('firebase/app'),
        import('firebase/messaging'),
      ])
      const app = getApps()[0] ?? initializeApp(config.firebase as FirebaseOptions)
      return getMessaging(app)
    })()
  }
  return messagingPromise
}

// The portal's own service worker (public/service-worker.js) handles the push events: one origin
// has a single worker for scope '/', so Firebase's default firebase-messaging-sw.js is not used.
// register() returns the existing registration when the same worker is already registered.
async function getServiceWorkerRegistration(): Promise<ServiceWorkerRegistration> {
  await navigator.serviceWorker.register(SERVICE_WORKER_URL)
  return navigator.serviceWorker.ready
}

async function requestToken(config: PushConfig): Promise<string> {
  const [{getToken}, messaging, serviceWorkerRegistration] = await Promise.all([
    import('firebase/messaging'),
    getMessagingInstance(config),
    getServiceWorkerRegistration(),
  ])
  return getToken(messaging, {vapidKey: config.vapidKey, serviceWorkerRegistration})
}

// The user's devices after a PUT /user-data/device, with the same rules as psp-backend (RF5): the
// store is updated locally instead of reading the whole user again. Dates are the browser's, the
// saved ones the server's: only the store is affected, until the next GET /userinfo.
export function applyDeviceUpdate(webDeviceTokens: WebDeviceToken[], {token, previousToken, label}: PutUserDeviceBody, now: string): WebDeviceToken[] {
  const withLabel = (device: WebDeviceToken): WebDeviceToken => (label ? {...device, label} : device)
  let next: WebDeviceToken[]
  if (token === null) {
    next = webDeviceTokens.filter(device => device.token !== previousToken)
  } else if (webDeviceTokens.some(device => device.token === token)) {
    next = webDeviceTokens.map(device => (device.token === token ? withLabel({...device, lastSeenAt: now}) : device))
  } else if (previousToken && webDeviceTokens.some(device => device.token === previousToken)) {
    next = webDeviceTokens.map(device => (device.token === previousToken ? withLabel({...device, token, lastSeenAt: now}) : device))
  } else {
    next = [...webDeviceTokens, withLabel({token, createdAt: now, lastSeenAt: now})]
  }
  while (next.length > MAX_DEVICES) {
    const oldest = next.reduce((a, b) => (Date.parse(b.lastSeenAt) < Date.parse(a.lastSeenAt) ? b : a))
    next = next.filter(device => device !== oldest)
  }
  return next
}

async function saveToken(body: PutUserDeviceBody): Promise<void> {
  await apiClient.putUserDevice(body)
  writeItem(LAST_TOKEN_KEY, body.token)
  writeItem(LAST_TOKEN_USER_KEY, body.token ? currentUserId() : null)
  const current = store.getState().userReducer.data?.webDeviceTokens ?? []
  const next = applyDeviceUpdate(current, body, new Date().toISOString())
  dispatch(setWebDeviceTokens(next.length ? next : undefined))
}

function previousTokenOf(lastToken: string | null): {previousToken?: string} {
  return lastToken ? {previousToken: lastToken} : {}
}

// Whether the device must be saved again (RF7): Firebase rotated its token, it is no longer among
// the user's devices (concurrent write or dropped by the limit of 5), or its lastSeenAt is more
// than a day old. Otherwise no call: at most one write a day per device.
export function needsTokenSync(lastToken: string, currentToken: string, webDeviceTokens: WebDeviceToken[] | undefined, now: number = Date.now()): boolean {
  if (currentToken !== lastToken) return true
  const device = (webDeviceTokens ?? []).find(item => item.token === lastToken)
  if (!device) return true
  const lastSeenAt = Date.parse(device.lastSeenAt)
  return Number.isNaN(lastSeenAt) || now - lastSeenAt > LAST_SEEN_REFRESH_MS
}

// What the browser and the environment allow, before looking at the user's devices
async function getAvailability(): Promise<PushState | 'available'> {
  if (!hasPushApis()) {
    return isIos() && !isStandalone() ? 'ios-install-required' : 'unsupported'
  }
  const config = await loadPushConfig()
  if (!config.enabled) {
    return 'not-configured'
  }
  const {isSupported} = await import('firebase/messaging')
  if (!(await isSupported())) {
    return 'unsupported'
  }
  if (Notification.permission === 'denied') {
    return 'denied'
  }
  return 'available'
}

export async function getPushState(userWebDeviceTokens?: WebDeviceToken[]): Promise<PushState> {
  const availability = await getAvailability()
  if (availability !== 'available') {
    return availability
  }
  const lastToken = readLastToken()
  const isThisDevice = Notification.permission === 'granted' && Boolean(lastToken) && tokensOf(userWebDeviceTokens).includes(lastToken as string)
  return isThisDevice ? 'active' : 'inactive'
}

// Must be called from a user gesture (a click): browsers block permission prompts otherwise.
// Resolves with the resulting state ('denied' if the user refused the prompt). The user's other
// devices stay active.
export async function enablePush(): Promise<PushState> {
  const permission = await Notification.requestPermission()
  if (permission !== 'granted') {
    return permission === 'denied' ? 'denied' : 'inactive'
  }
  const config = await loadPushConfig()
  const token = await requestToken(config)
  await saveToken({token, ...previousTokenOf(readLastToken()), label: getDeviceLabel()})
  return 'active'
}

// Stops the reminders on this device only: its token is removed from the user's devices.
export async function disablePush(): Promise<void> {
  const lastToken = readLastToken()
  if (!lastToken) {
    return
  }
  await saveToken({token: null, previousToken: lastToken})
  try {
    const config = await loadPushConfig()
    const [{deleteToken}, messaging] = await Promise.all([import('firebase/messaging'), getMessagingInstance(config)])
    await deleteToken(messaging)
  } catch (error) {
    // best effort: the token is no longer saved on the user, so no reminder reaches it anyway
  }
}

// Called once the session is ready, on a device where notifications were enabled: re-reads the
// token (Firebase rotates them over time) and saves it again when needsTokenSync says so.
// A token missing from the list is registered again only for the user who registered it on this
// browser: if another user enabled notifications here afterwards, the token is now theirs
// (psp-backend took it away from this user) and is left alone.
export async function syncPushToken(userWebDeviceTokens?: WebDeviceToken[]): Promise<void> {
  const lastToken = readLastToken()
  if (!lastToken) {
    return
  }
  const owner = readItem(LAST_TOKEN_USER_KEY)
  if (!tokensOf(userWebDeviceTokens).includes(lastToken) && (!owner || owner !== currentUserId())) {
    return
  }
  if ((await getAvailability()) !== 'available' || Notification.permission !== 'granted') {
    return
  }
  const config = await loadPushConfig()
  const token = await requestToken(config)
  if (needsTokenSync(lastToken, token, userWebDeviceTokens)) {
    await saveToken({token, previousToken: lastToken, label: getDeviceLabel()})
  }
}
